import { FC, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import isString from 'lodash/isString'
import { FormInput } from '@microservices/wiskey-react-components'
import { Grid, useTheme } from '@mui/material'
import { useSearchAssistantContext } from '@widgets/SearchAssistant/context'

import { useFetchContextViewsByIdQuery, useFetchDefaultViewSAQuery } from '@redux/api'

import { useAppDispatch } from '@hooks'
import { generalFieldStylesByMode, showSnackbarNotification } from '@helpers'
import { GENERATOR_INPUT_TYPE } from '@constants'
import { AutocompleteOption, EntityType } from '@types'

type SelectedEntity = Pick<EntityType, 'code' | 'id'> | null

interface SearchAssistantViewSelectorProps {
  entityId: number | string | null
  onClear: () => void
  onSelectEntity: (entity: SelectedEntity) => void
  contextId: string | number | undefined
  defaultViewIdSA?: string | number | undefined | null
  searchAssistantId?: string | number | undefined | null
  loadingContextSA?: boolean
}

const SearchAssistantViewSelector: FC<SearchAssistantViewSelectorProps> = props => {
  const {
    entityId,
    onClear,
    onSelectEntity,
    contextId,
    defaultViewIdSA,
    searchAssistantId,
    loadingContextSA,
  } = props

  const context = useSearchAssistantContext('SearchAssistantViewSelector')

  const { data: defaultView, isLoading: isLoadingDefaultViewSA } = useFetchDefaultViewSAQuery(
    {
      contextId,
    },
    {
      skip: !defaultViewIdSA || !contextId,
      refetchOnMountOrArgChange: true,
    }
  )

  const { data: contextViewsById, isLoading: isLoadingContextViews } =
    useFetchContextViewsByIdQuery(contextId, {
      skip: !contextId,
      refetchOnMountOrArgChange: true,
    })

  const methods = useForm<{
    viewId: AutocompleteOption | null
  }>({
    defaultValues: { viewId: null },
  })

  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { watch, setValue } = methods
  const watchViewId = watch('viewId')

  const viewOptions = useMemo(
    () =>
      contextViewsById?.selectedViews?.map(view => ({
        id: view.id,
        label: view.title,
        code: view.code,
      })),
    [contextViewsById]
  )

  useEffect(() => {
    setValue(
      'viewId',
      viewOptions
        ? (viewOptions?.find(option => option.id === entityId) as AutocompleteOption)
        : null
    )
  }, [])

  useEffect(() => {
    if (!watchViewId) {
      onClear()
    }

    if (watchViewId && isString(watchViewId.label)) {
      onSelectEntity({ id: watchViewId.id, code: watchViewId.code ?? watchViewId.label })

      return
    }

    onSelectEntity(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchViewId?.id, watchViewId?.label])

  useEffect(() => {
    setValue(
      'viewId',
      defaultView ? { id: defaultView.id, label: defaultView.title, code: defaultView.code } : null
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultView])

  useEffect(() => {
    if (defaultViewIdSA === null && !loadingContextSA) {
      showSnackbarNotification({
        text: t('error.noDefaultViewInContext'),
        options: {
          variant: 'info',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultViewIdSA, loadingContextSA])

  useEffect(() => {
    if (searchAssistantId === null && !loadingContextSA) {
      showSnackbarNotification({
        text: t('error.noSearchAssistantInContext'),
        options: {
          variant: 'info',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchAssistantId, loadingContextSA])

  return (
    <FormProvider {...methods}>
      <form>
        <Grid
          container
          alignItems='center'
          justifyContent='space-between'
          sx={{ width: '100%', px: 1 }}
        >
          <FormInput
            {...{
              name: 'viewId',
              inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
              placeholder: t('searchAssistant.filtration.view.placeholder'),
              autocompleteOptions: viewOptions,
              label: t('searchAssistant.filtration.view.label'),
              // TODO(isilanov): Заменить на состояние загрузки изменив визуал скелетона в FormGenerator
              disabled: isLoadingDefaultViewSA || isLoadingContextViews,
              sx: {
                '& > .MuiFormControl-root': {
                  ...generalFieldStylesByMode(
                    theme,
                    undefined,
                    undefined,
                    undefined,
                    context.isInDialogWindow
                  ),
                },
                '& .MuiTypography-root': {
                  minWidth: 90,
                  width: 90,
                },
              },
              labelSx: { width: 90, minWidth: 90 },
            }}
          />
        </Grid>
      </form>
    </FormProvider>
  )
}

SearchAssistantViewSelector.displayName = 'SearchAssistantViewSelector'

export { SearchAssistantViewSelector }
